import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { TablePagination } from "@/components/TablePagination";
import { Send, Check, X, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 10;
const STATUSES = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "cancelled", label: "Cancelled" },
];

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_ICON = {
  pending: Clock,
  approved: Check,
  rejected: X,
  cancelled: X,
};

function safe(v, fallback = "—") {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? safe(iso) : d.toLocaleString();
}

/**
 * Admin-side view of the requests **this admin** has sent.
 *
 * The user's own Settings tab is fed by `GET /auth/permission-requests`, which
 * only ever contains requests addressed *to* them. An admin who has requested
 * access from users therefore sees an empty inbox there, which reads as
 * "nothing happened". This panel shows the outgoing side and each user's
 * answer, so the round trip is visible in one place.
 */
export default function OutgoingRequests() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (statusFilter, skip, size) => {
    setLoading(true);
    try {
      const params = { mine: true, limit: size, skip: skip || 0 };
      if (statusFilter) params.status = statusFilter;
      const [{ data }, count] = await Promise.all([
        api.get("/admin/permission-requests", { params }),
        api.get("/admin/permission-requests/count", {
          params: { mine: true, ...(statusFilter ? { status: statusFilter } : {}) },
        }),
      ]);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.entries) ? data.entries : []);
      setRows(list);
      setTotal(count?.data?.count ?? list.length);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // One effect, all inputs. `changeStatus`/`changePageSize` reset the page in
  // the same handler so React batches them into a single fetch instead of one
  // for the stale page plus one for the corrected one.
  useEffect(() => {
    load(status, page * pageSize, pageSize);
  }, [status, page, pageSize, load]);

  const cancel = async (id) => {
    if (!window.confirm("Withdraw this request? The user will no longer see it as pending.")) return;
    setBusy(id);
    try {
      await api.post(`/admin/permission-requests/${id}/cancel`);
      toast.success("Request withdrawn");
      await load(status, page * pageSize, pageSize);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const changeStatus = (id) => { setStatus(id); setPage(0); };
  const changePageSize = (n) => { setPageSize(n); setPage(0); };

  return (
    <div className="mt-6" data-testid="outgoing-requests">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-medium text-sm">Requests you have sent</div>
          <p className="text-xs text-gray-500 mt-0.5">
            Asking for access never grants it. Each user answers for themselves.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s.id || "all"}
              onClick={() => changeStatus(s.id)}
              className={`text-[11px] px-2.5 h-7 border rounded-full ${
                status === s.id
                  ? "bg-[var(--fg)] text-white border-[var(--fg)]"
                  : "border-[var(--border)] hover:bg-white"
              }`}
              data-testid={`outgoing-filter-${s.id || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="card-flat p-8 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your requests…
        </div>
      ) : rows.length === 0 ? (
        <div className="card-flat p-8 text-center" data-testid="outgoing-empty">
          <Send className="w-6 h-6 text-gray-300 mx-auto" />
          <div className="text-sm font-medium mt-2">
            {status ? `No ${status} requests` : "You have not requested access to anyone's data"}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Use “Request access” on a user in the Admin page to ask.
          </p>
        </div>
      ) : (
        <div className="card-flat divide-y divide-[var(--border)]" data-testid="outgoing-list">
          {rows.map((r) => {
            const Icon = STATUS_ICON[r.status] || Clock;
            const isPending = r.status === "pending";
            return (
              <div key={safe(r.id)} className="p-4" data-testid={`outgoing-${safe(r.id)}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm">
                      <strong>{safe(r.target_email, "Unknown user")}</strong>
                      <span className="text-gray-500"> · asked for </span>
                      <strong>{r.scope === "modify" ? "view and edit" : "view"}</strong>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      Sent {when(r.created_at)}
                      {r.responded_at ? ` · answered ${when(r.responded_at)}` : ""}
                    </div>
                    {r.reason ? (
                      <div className="text-xs text-gray-600 mt-2 p-2 rounded bg-gray-50 border border-[var(--border)]">
                        “{safe(r.reason, "")}”
                      </div>
                    ) : null}
                    {r.response_note ? (
                      <div className="text-xs text-gray-700 mt-2 p-2 rounded bg-blue-50 border border-blue-100">
                        <span className="font-medium">Their note:</span> {safe(r.response_note, "")}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] border inline-flex items-center gap-1 ${
                        STATUS_STYLE[r.status] || STATUS_STYLE.cancelled
                      }`}
                      data-testid={`outgoing-status-${safe(r.id)}`}
                    >
                      <Icon className="w-3 h-3" /> {safe(r.status)}
                    </span>
                    {isPending && (
                      <button
                        onClick={() => cancel(r.id)}
                        disabled={busy === r.id}
                        className="text-[11px] h-7 px-2.5 border border-[var(--border)] rounded-md hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`outgoing-cancel-${safe(r.id)}`}
                      >
                        {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 0 && (
        <TablePagination
          page={page}
          pageCount={Math.ceil(total / pageSize)}
          total={total}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50]}
          onPage={setPage}
          onPageSize={changePageSize}
          idPrefix="outgoing"
          noun="request"
        />
      )}
    </div>
  );
}